import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Brain, Home, FileText, BookOpen, Users, Menu } from 'lucide-react';

interface SimpleNavigationProps {
  currentPage: 'landing' | 'questions';
  onPageChange: (page: 'landing' | 'questions') => void;
}

const SimpleNavigation: React.FC<SimpleNavigationProps> = ({
  currentPage,
  onPageChange,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (currentScrollY < lastScrollY) {
        // Scrolling up - expand
        setIsExpanded(true);
      } else if (currentScrollY > lastScrollY && currentScrollY > 100) {
        // Scrolling down and past 100px - collapse
        setIsExpanded(false);
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  return (
    <>
      <motion.nav
        initial={{ opacity: 0, x: 100 }}
        animate={{
          opacity: 1,
          x: 0,
          width: isExpanded ? 'auto' : '60px',
        }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
        className="fixed top-2 sm:top-4 right-2 sm:right-4 z-40 max-w-[calc(100vw-1rem)] sm:max-w-[calc(100vw-2rem)]"
      >
        <motion.div
          className="bg-white/95 backdrop-blur-md rounded-full shadow-2xl border border-white/30 overflow-hidden"
          animate={{
            paddingLeft: isExpanded ? '8px' : '6px',
            paddingRight: isExpanded ? '8px' : '6px',
            paddingTop: '6px',
            paddingBottom: '6px',
          }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
        >
          <div className="flex items-center gap-4">
            {/* Logo - Always visible */}
            <motion.div
              className="flex items-center gap-2 cursor-pointer"
              onClick={() => setIsExpanded(!isExpanded)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <div className="w-6 h-6 sm:w-8 sm:h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center flex-shrink-0">
                <Brain className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
              </div>
              <AnimatePresence>
                {isExpanded && (
                  <motion.span
                    initial={{ opacity: 0, width: 0 }}
                    animate={{ opacity: 1, width: 'auto' }}
                    exit={{ opacity: 0, width: 0 }}
                    transition={{ duration: 0.2 }}
                    className="font-bold text-gray-800 whitespace-nowrap"
                  >
                    IQ Test
                  </motion.span>
                )}
              </AnimatePresence>
            </motion.div>

            {/* Navigation items - Only show when expanded */}
            <AnimatePresence>
              {isExpanded && (
                <motion.div
                  initial={{ opacity: 0, width: 0 }}
                  animate={{ opacity: 1, width: 'auto' }}
                  exit={{ opacity: 0, width: 0 }}
                  transition={{ duration: 0.2, delay: 0.1 }}
                  className="flex items-center gap-2 md:gap-4"
                >
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => onPageChange('landing')}
                    className={`flex items-center justify-center gap-1 sm:gap-2 px-2 sm:px-3 py-1 sm:py-2 rounded-full font-medium text-sm sm:text-base transition-all duration-300 ${
                      currentPage === 'landing'
                        ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg'
                        : 'text-gray-600 hover:text-purple-600 hover:bg-purple-50'
                    }`}
                  >
                    <div className="flex items-center justify-center">
                      <Home size={14} className="sm:w-4 sm:h-4" />
                    </div>
                    <span className="hidden sm:inline">Home</span>
                  </motion.button>

                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => onPageChange('questions')}
                    className={`flex items-center justify-center gap-1 sm:gap-2 px-2 sm:px-3 py-1 sm:py-2 rounded-full font-medium text-sm sm:text-base transition-all duration-300 ${
                      currentPage === 'questions'
                        ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg'
                        : 'text-gray-600 hover:text-purple-600 hover:bg-purple-50'
                    }`}
                  >
                    <div className="flex items-center justify-center">
                      <FileText size={14} className="sm:w-4 sm:h-4" />
                    </div>
                    <span className="hidden sm:inline">Sample Test</span>
                  </motion.button>

                  <motion.a
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    href="/iq-guide"
                    className="flex items-center justify-center gap-1 sm:gap-2 px-2 sm:px-3 py-1 sm:py-2 rounded-full font-medium text-sm sm:text-base transition-all duration-300 text-gray-600 hover:text-purple-600 hover:bg-purple-50"
                  >
                    <div className="flex items-center justify-center">
                      <BookOpen size={14} className="sm:w-4 sm:h-4" />
                    </div>
                    <span className="hidden sm:inline">IQ Guide</span>
                  </motion.a>

                  <motion.a
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    href="/blog"
                    className="flex items-center justify-center gap-1 sm:gap-2 px-2 sm:px-3 py-1 sm:py-2 rounded-full font-medium text-sm sm:text-base transition-all duration-300 text-gray-600 hover:text-purple-600 hover:bg-purple-50"
                  >
                    <div className="flex items-center justify-center">
                      <Menu size={14} className="sm:w-4 sm:h-4" />
                    </div>
                    <span className="hidden sm:inline">Blog</span>
                  </motion.a>

                  <motion.a
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    href="/about"
                    className="flex items-center justify-center gap-1 sm:gap-2 px-2 sm:px-3 py-1 sm:py-2 rounded-full font-medium text-sm sm:text-base transition-all duration-300 text-gray-600 hover:text-purple-600 hover:bg-purple-50"
                  >
                    <div className="flex items-center justify-center">
                      <Users size={14} className="sm:w-4 sm:h-4" />
                    </div>
                    <span className="hidden sm:inline">About</span>
                  </motion.a>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </motion.nav>


    </>
  );
};

export default SimpleNavigation;
